import React, { useEffect } from 'react';
import { CATEGORIES, catColor, categorizeEvent } from '../../config/categories';
import { formatEventHostsLabel, formatEventWhen, getEventHosts, isEventCanceled, parseEventDate } from '../../utils/eventUtils';
import { fs } from '../../utils/typography';
import RegistrationLinks from '../RegistrationLinks';

// Mobile week-view preview, shown inside a MobileSheet when an event block is
// tapped, so people can see what a cramped block is without being taken to
// the day pane. Tapping the preview (or "Details") opens the full details;
// the ✕, Escape, dragging the sheet down, or a tap on empty grid space closes it.
const WeekEventPreview = ({ event, day, tokens, onOpenDetails, onClose }) => {
    const c = tokens.colors;
    const cat = categorizeEvent(event);
    const cc = catColor(cat, tokens);
    const canceled = isEventCanceled(event);
    const eventEnd = parseEventDate(event.endDate || event.startDate);
    const isUpcoming = !canceled && eventEnd && eventEnd >= new Date(new Date().setHours(0, 0, 0, 0));
    const showRegistration = isUpcoming && getEventHosts(event).some((host) => host.eventURL);
    const dayLabel = day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const buttonBase = {
        fontFamily: tokens.fonts.ui, fontSize: fs(13), fontWeight: 700, borderRadius: 9,
        padding: '8px 14px', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center',
    };

    return (
        <div
            role="dialog"
            aria-label={`Preview: ${event.title}`}
            onClick={(e) => { e.stopPropagation(); onOpenDetails(); }}
            style={{ padding: '2px 20px 16px', cursor: 'pointer', textAlign: 'left' }}
        >
            <div className="flex items-center justify-between gap-2">
                <span style={{
                    fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.5, textTransform: 'uppercase',
                    fontWeight: 600, color: cc.fillText, background: cc.fillBg, border: `1px solid ${cc.line}`,
                    padding: '2px 7px', borderRadius: 6,
                }}>
                    <i className={`fas ${CATEGORIES[cat].icon}`} style={{ marginRight: 4 }} aria-hidden="true" />
                    {CATEGORIES[cat].label}
                </span>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    aria-label="Close preview"
                    style={{ width: 30, height: 30, marginRight: -6, color: c.faint, background: 'transparent', border: 'none', fontSize: fs(15) }}
                >
                    <i className="fas fa-times" aria-hidden="true" />
                </button>
            </div>

            <div style={{
                fontFamily: tokens.fonts.ui, fontSize: fs(16), fontWeight: 700, color: c.ink, lineHeight: 1.25,
                marginTop: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                opacity: canceled ? 0.6 : 1,
            }}>
                {canceled && <i className="fas fa-ban" style={{ color: c.faint, fontSize: fs(12), marginRight: 5 }} aria-label="Canceled event" />}
                {!canceled && event.eventType === 'Featured' && <i className="fas fa-star" style={{ color: cc.strong, fontSize: fs(12), marginRight: 5 }} aria-label="Featured event" />}
                <span style={{ textDecoration: canceled ? 'line-through' : 'none' }}>{event.title}</span>
            </div>

            <div style={{ fontFamily: tokens.fonts.mono, fontSize: fs(11), fontWeight: 600, color: cc.strong, marginTop: 4 }}>
                {dayLabel} · {formatEventWhen(event)}
            </div>
            <div style={{ fontFamily: tokens.fonts.ui, fontSize: fs(12.5), color: c.muted, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatEventHostsLabel(event)}
            </div>
            {event.location && (
                <div style={{ fontFamily: tokens.fonts.ui, fontSize: fs(12), color: c.faint, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <i className="fas fa-map-marker-alt" style={{ marginRight: 5 }} aria-hidden="true" />{event.location}
                </div>
            )}

            <div className="flex items-center flex-wrap gap-2" style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
                {canceled ? (
                    <span style={{ ...buttonBase, border: `1px solid ${c.gridLine}`, color: c.faint }}>
                        <i className="fas fa-ban mr-2" aria-hidden="true" />Canceled
                    </span>
                ) : (
                    <RegistrationLinks
                        event={event}
                        showLinks={showRegistration}
                        variant="register"
                        buttonStyle={{ ...buttonBase, background: c.accent, color: c.todayInk }}
                        singleContainerClassName="flex items-center gap-2"
                        multipleContainerClassName="flex items-center flex-wrap gap-2"
                    />
                )}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenDetails(); }}
                    style={{ ...buttonBase, border: `1px solid ${c.gridLine}`, color: c.ink, background: 'transparent' }}
                >
                    Details<i className="fas fa-chevron-right" style={{ marginLeft: 6, fontSize: fs(10) }} aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

export default WeekEventPreview;
